/**
 * <PositionPicker /> — typeahead selector for picking ONE position.
 *
 * Mirrors <EmployeePicker /> but for positions. Searches by job
 * title (EN+AR) and department name. Shows position number, title,
 * department, and current assignee in the dropdown.
 *
 * Requires legalEntityId (positions are scoped per LE).
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Briefcase, ChevronDown, Search, X } from "lucide-react";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { api, type PositionListItem } from "../../lib/api";

export interface PositionPickerProps {
  value?: string | null;
  onChange: (pos: PositionListItem | null) => void;
  legalEntityId: string;
  status?: "open" | "filled" | "draft";
  placeholder?: string;
  disabled?: boolean;
  selected?: PositionListItem | null;
}

export function PositionPicker({
  value,
  onChange,
  legalEntityId,
  status,
  placeholder,
  disabled,
  selected: selectedProp,
}: PositionPickerProps) {
  const { t } = useTranslation();
  const inputId = useId();
  const effectivePlaceholder =
    placeholder ?? t("app.ppp_placeholder_default");
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef<HTMLDivElement>(null);

  // Fully controlled: `selected` is derived from `value` via the
  // cached list. Reorder swaps + initial loads stay consistent
  // without internal state to drift out of sync.
  const hydrationQ = useQuery({
    queryKey: ["position-picker-hydrate", legalEntityId],
    enabled: !!value && !selectedProp && !!legalEntityId,
    staleTime: 60_000,
    queryFn: () => api.listPositions(legalEntityId, {}),
  });
  const selected: PositionListItem | null = (() => {
    if (selectedProp !== undefined && selectedProp !== null) return selectedProp;
    if (!value) return null;
    return hydrationQ.data?.find((p) => p.id === value) ?? null;
  })();

  const [debouncedQ, setDebouncedQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(query), 200);
    return () => clearTimeout(t);
  }, [query]);

  const resultsQ = useQuery({
    queryKey: [
      "position-picker-search",
      debouncedQ,
      legalEntityId,
      status,
    ],
    enabled: open && !!legalEntityId,
    queryFn: () =>
      api.listPositions(legalEntityId, {
        q: debouncedQ || undefined,
        status,
      }),
  });
  const results = resultsQ.data ?? [];

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  const handlePick = useCallback(
    (p: PositionListItem) => {
      qc.setQueryData<PositionListItem[] | undefined>(
        ["position-picker-hydrate", legalEntityId],
        (prev) => {
          if (!prev) return [p];
          if (prev.some((x) => x.id === p.id)) return prev;
          return [...prev, p];
        },
      );
      onChange(p);
      setOpen(false);
      setQuery("");
    },
    [onChange, qc, legalEntityId],
  );

  const handleClear = useCallback(() => {
    onChange(null);
    setQuery("");
  }, [onChange]);

  return (
    <div ref={wrapRef} className="relative w-full">
      {selected && !open ? (
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen(true)}
          className="w-full flex items-center gap-2 rounded-lg border border-border-subtle bg-surface-default px-3 py-2 text-start hover:border-brand-blue/50 disabled:opacity-50"
        >
          <div className="rounded bg-brand-blue/10 p-1.5">
            <Briefcase size={16} className="text-brand-blue" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="truncate text-sm font-medium text-text-primary">
              {selected.title_en}
            </div>
            <div className="truncate text-xs text-text-tertiary">
              <span className="font-mono">
                {selected.position_number_display}
              </span>{" "}
              · {selected.department_name_en}
              {selected.current_assignee && (
                <span> · {selected.current_assignee.first_name_en}{" "}
                {selected.current_assignee.last_name_en}</span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            className="p-1 rounded hover:bg-neutral-100"
            aria-label={t("app.ppp_clear_selection")}
          >
            <X size={14} className="text-text-tertiary" />
          </button>
          <ChevronDown size={14} className="text-text-tertiary" />
        </button>
      ) : (
        <div className="flex items-center gap-1.5 rounded-lg border border-border-subtle bg-surface-default px-3 py-2 focus-within:border-brand-blue">
          <Search size={14} className="text-text-tertiary shrink-0" />
          <input
            id={inputId}
            type="text"
            autoFocus={open}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            placeholder={effectivePlaceholder}
            disabled={disabled || !legalEntityId}
            data-testid="position-picker-search"
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-text-tertiary"
          />
          {selected && (
            <button
              type="button"
              onClick={handleClear}
              className="p-0.5 rounded hover:bg-neutral-100"
              aria-label={t("app.ppp_clear")}
            >
              <X size={14} className="text-text-tertiary" />
            </button>
          )}
        </div>
      )}

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-border-subtle bg-white shadow-xl max-h-64 overflow-y-auto">
          {resultsQ.isLoading && (
            <p className="px-3 py-2 text-xs text-text-tertiary">
              {t("app.gs_searching")}
            </p>
          )}
          {!resultsQ.isLoading && results.length === 0 && (
            <p className="px-3 py-2 text-xs text-text-tertiary">
              {debouncedQ ? t("app.ppp_no_matches") : t("app.ppp_start_typing")}
            </p>
          )}
          {results.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => handlePick(p)}
              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-neutral-50 text-start"
            >
              <div className="rounded bg-brand-blue/10 p-1.5">
                <Briefcase size={14} className="text-brand-blue" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="truncate text-sm font-medium text-text-primary">
                  {p.title_en}
                </div>
                <div className="truncate text-xs text-text-tertiary">
                  <span className="font-mono">
                    {p.position_number_display}
                  </span>{" "}
                  · {p.department_name_en}
                  {p.current_assignee && (
                    <span> · {p.current_assignee.first_name_en}{" "}
                    {p.current_assignee.last_name_en}</span>
                  )}
                </div>
              </div>
              <span className="text-xs text-text-tertiary capitalize">
                {p.status}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
